import { Box, IconButton, Tooltip, Typography, useTheme } from "@mui/material"
import { TimePicker } from "@mui/x-date-pickers/TimePicker"
import { DateTime } from "luxon"
import DeleteIcon from "@mui/icons-material/Delete"
import AddIcon from "@mui/icons-material/Add"
import { Transition, DailySchedule, INITIAL_STATE_ARMED, TimeOfDay } from "../../../api/AutoArmConfig"
import { useTranslation } from "react-i18next"
import { NotificationsOffOutlined, NotificationsOutlined } from "@mui/icons-material"
import { useCallback, useEffect, useState } from "react"

type Props = {
    value: DailySchedule
    readOnly: boolean
    onChange: (newSchedule: DailySchedule) => void
    onError: (hasError: boolean) => void
}

const normalizeTransitionStates = (transitions: Transition[], initial: number): Transition[] => {
    return transitions.map((t, i) => ({
        time: t.time,
        value: i % 2 === 0 ? (initial ? 0 : 1) : initial ? 1 : 0,
    }))
}

export const ScheduleSetter = ({ value, readOnly, onChange, onError }: Props) => {
    const { t } = useTranslation()
    const theme = useTheme()
    const [error, setError] = useState("")

    const handleError = useCallback((err: string) => {
        setError((prev) => (prev === err ? prev : err))
    }, [])

    useEffect(() => {
        onError(!!error)
    }, [error, onError])

    useEffect(() => {
        const transitions = value.transitions

        const timeSet = new Set(transitions.map((t) => `${t.time.hours}:${t.time.minutes}:${t.time.seconds}`))
        if (timeSet.size !== transitions.length) {
            handleError(t("dialog.auto_arm.each_schedule_must_have_a_unique_time"))
            return
        }

        const toSeconds = (t: TimeOfDay) => (t.hours ?? 0) * 3600 + (t.minutes ?? 0) * 60

        const initialTime = 0
        const initialVal = INITIAL_STATE_ARMED
        for (let i = 0; i < transitions.length; i++) {
            const prevTime = i === 0 ? initialTime : toSeconds(transitions[i - 1].time)
            const currTime = toSeconds(transitions[i].time)

            const prevVal = i === 0 ? initialVal : transitions[i - 1].value
            const currVal = transitions[i].value

            if (currTime <= prevTime) {
                handleError(t("dialog.auto_arm.each_time_must_be_later_than_previous_time"))
                return
            }

            if (prevVal === currVal) {
                handleError(t("dialog.auto_arm.each_transition_must_be_opposite_of_the_previous_one"))
                return
            }
        }

        handleError("")
    }, [value.transitions, handleError, t])

    const handleTimeChange = (index: number, newTime: DateTime | null) => {
        if (!newTime || !newTime.isValid) {
            handleError("Invalid time.")
            return
        }
        const updated = [...value.transitions]
        updated[index].time = {
            hours: newTime.hour,
            minutes: newTime.minute,
            seconds: newTime.second,
        }
        onChange({ ...value, transitions: updated })
    }

    const handleAddTransition = (index: number = value.transitions.length - 1) => {
        const newTransitions = [...value.transitions]
        const newTransition: Transition = {
            time: { hours: 0, minutes: 0, seconds: 0 },
            value: 0,
        }
        newTransitions.splice(index + 1, 0, newTransition)

        const normalized = normalizeTransitionStates(newTransitions, value.initial)
        onChange({ ...value, transitions: normalized })
    }

    const handleRemoveTransition = (index: number) => {
        const updated = value.transitions.filter((_, i) => i !== index)
        const normalized = normalizeTransitionStates(updated, value.initial)
        onChange({ ...value, transitions: normalized })
    }

    const renderTransitionItem = (transition: Transition, index: number) => {
        const time = DateTime.fromObject({
            hour: transition.time.hours ?? 0,
            minute: transition.time.minutes ?? 0,
            second: transition.time.seconds ?? 0,
        })

        return (
            <Box key={index} display="flex" alignItems="center" gap={2} mt={1}>
                {transition.value ? (
                    <NotificationsOutlined sx={{ color: theme.palette.secondary.main }} />
                ) : (
                    <NotificationsOffOutlined sx={{ color: theme.palette.success.main }} />
                )}

                <TimePicker
                    label={t("time")}
                    value={time}
                    onChange={(newTime) => handleTimeChange(index, newTime)}
                    views={["hours", "minutes"]}
                    ampm={false}
                    disabled={readOnly}
                    slotProps={{
                        textField: {
                            inputProps: {
                                readOnly: true,
                            },
                        },
                    }}
                />

                <IconButton
                    sx={{ color: theme.palette.primary.main }}
                    disabled={readOnly}
                    onClick={() => handleAddTransition(index)}
                >
                    <AddIcon />
                </IconButton>

                <IconButton
                    sx={{ color: theme.palette.primary.main }}
                    disabled={readOnly}
                    onClick={() => handleRemoveTransition(index)}
                >
                    <DeleteIcon />
                </IconButton>
            </Box>
        )
    }

    return (
        <Box mt={2}>
            <Tooltip title={t("dialog.auto_arm.default_transition")}>
                <Box display="flex" alignItems="center" gap={2} mt={1}>
                    {value.initial ? (
                        <NotificationsOutlined sx={{ color: theme.palette.secondary.main }} />
                    ) : (
                        <NotificationsOffOutlined sx={{ color: theme.palette.success.main }} />
                    )}

                    <TimePicker
                        label={t("time")}
                        value={DateTime.fromObject({ hour: 0, minute: 0 })}
                        views={["hours", "minutes"]}
                        ampm={false}
                        disabled
                    />

                    <IconButton
                        sx={{ color: theme.palette.primary.main }}
                        disabled={readOnly}
                        onClick={() => handleAddTransition(-1)}
                    >
                        <AddIcon />
                    </IconButton>
                </Box>
            </Tooltip>

            {value.transitions.map((transition, index) => renderTransitionItem(transition, index))}

            <Box mt={2} display="flex" alignItems="center" gap={2}>
                {error && (
                    <Typography fontSize="small" variant="body2" color={theme.palette.error.main} paddingRight={2}>
                        {error}
                    </Typography>
                )}
            </Box>
        </Box>
    )
}
